import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { PrintProbe, PrintProbeLookup } from '../../lib/printProbe'
import type { GeoPt } from '../../lib/types'

/** Debounce window after the map's centre moves before a lookup fires — a
 *  pan gesture crosses many points per second, and Estate/Tanger shouldn't
 *  see one request per frame. */
const DEBOUNCE_MS = 350

/**
 * Rounds a centre to a 2-decimal-degree cell (roughly 1km at mid
 * latitudes). Every centre inside the same cell shares one lookup: the
 * print frame is many kilometres wide, so a centre drifting by metres
 * while the user fine-tunes the map essentially never crosses a national
 * border, and re-asking the print server for the same answer on every such
 * nudge would be pure waste.
 *
 * Exported so a consumer can key the LIVE centre the same way the answer is
 * keyed, and so tell "this answer describes where the map is now" from "this
 * answer describes where the map used to be".
 */
export function printProbeKey(center: GeoPt): string {
  return `${center.lat.toFixed(2)},${center.lon.toFixed(2)}`
}

export interface PrintProbeAnswer {
  /**
   * `undefined` before the first answer for the live surface, otherwise the
   * probe — `SUCCESS` with the CRS and what the server will print, or the
   * server's own `UNAVAILABLE`.
   */
  probe: PrintProbe | undefined
  /** The rounded centre key (see {@link printProbeKey}) `probe` answers for,
   *  or null while there is no answer. A consumer that acts on the probe —
   *  fitting the map to a frame drawn in that country's grid — compares it
   *  against the live centre's own key first: an answer for a cell the map has
   *  since left is still rendered (a stale frame beats a blank one) but must
   *  not drive a camera move that the arriving answer would immediately
   *  contradict.
   */
  key: string | null
  /** A lookup for the live centre is still needed or in flight. */
  pending: boolean
  /** Only a failure for the live centre is exposed. */
  error: boolean
  retry: () => void
}

/**
 * The print server's probe answer for the map's current centre, resolved
 * through `getPrintProbe` (the Estate API client — web never calls Tanger
 * directly).
 *
 * The lookup is debounced {@link DEBOUNCE_MS} after `center` last moved into
 * a new rounded cell (see {@link printProbeKey}); moving within the same
 * cell, even before the previous lookup has resolved, fires no additional
 * request. Answers are cached by their cell key for the hook's lifetime, so
 * revisiting a cell (panning away and back) never re-queries. A rejected
 * lookup leaves the previously known answer in place — a transient network
 * blip shouldn't blank out a frame the user was already looking at — and an
 * answer that arrives after the centre has moved on to a different cell is
 * dropped rather than overwriting the (now more current) state with stale
 * information.
 *
 * `enabled` is how a surface that outlives its own print mode (the map
 * editor, whose panel opens and closes while the page stays mounted) says
 * "nothing is looking at this right now": no lookups fire, and the current
 * answer is dropped rather than being handed back on the next open, when it
 * may describe a centre the user has since left. The cache is kept — a cell's
 * country does not change — so reopening at a known cell answers immediately.
 */
export function usePrintProbe(
  center: GeoPt | null,
  getPrintProbe?: PrintProbeLookup,
  enabled = true,
): PrintProbeAnswer {
  const [answer, setAnswer] = useState<{ key: string; probe: PrintProbe } | null>(null)
  const [failedKey, setFailedKey] = useState<string | null>(null)
  const [retryAttempt, setRetryAttempt] = useState(0)

  const cacheRef = useRef(new Map<string, PrintProbe>())
  const lastQueriedKeyRef = useRef<string | null>(null)
  const centerRef = useRef(center)
  centerRef.current = center
  const getPrintProbeRef = useRef(getPrintProbe)
  getPrintProbeRef.current = getPrintProbe
  const canQuery = getPrintProbe !== undefined

  const key = enabled && center ? printProbeKey(center) : null
  const liveKeyRef = useRef<string | null>(key)
  liveKeyRef.current = key
  const generationRef = useRef(0)
  const retry = useCallback(() => {
    generationRef.current++
    const liveKey = liveKeyRef.current
    if (liveKey) cacheRef.current.delete(liveKey)
    lastQueriedKeyRef.current = null
    setAnswer(null)
    setFailedKey(null)
    setRetryAttempt((attempt) => attempt + 1)
  }, [])

  useEffect(() => {
    if (!key) {
      setAnswer(null)
      setFailedKey(null)
      lastQueriedKeyRef.current = null
      return
    }
    if (!canQuery) return

    const cached = cacheRef.current.get(key)
    if (cached !== undefined) {
      lastQueriedKeyRef.current = key
      setAnswer({ key, probe: cached })
      return
    }

    if (key === lastQueriedKeyRef.current) return

    const timer = setTimeout(() => {
      const generation = generationRef.current
      setFailedKey(null)
      lastQueriedKeyRef.current = key
      const queriedCenter = centerRef.current
      const lookup = getPrintProbeRef.current
      if (!queriedCenter || !lookup) return

      lookup(queriedCenter)
        .then((probe) => {
          if (generation !== generationRef.current) return
          cacheRef.current.set(key, probe)
          if (liveKeyRef.current === key) {
            setFailedKey(null)
            setAnswer({ key, probe })
          }
        })
        .catch(() => {
          if (generation !== generationRef.current) return
          // Keep the old frame for orientation, but report the live failure
          // so submission remains disabled until Retry succeeds.
          //
          // The "already asked this cell" mark is dropped, though, or one blip
          // would silence that cell for the session: the mark is set before
          // the call so a cell the map leaves and re-enters mid-flight isn't
          // asked twice, and with nothing cached and the mark left standing,
          // re-entering the cell would find neither an answer nor permission
          // to ask again. Only this key's own mark is cleared — a later cell's
          // in-flight query has its own claim on it.
          if (lastQueriedKeyRef.current === key) lastQueriedKeyRef.current = null
          if (liveKeyRef.current === key) setFailedKey(key)
        })
    }, DEBOUNCE_MS)

    return () => clearTimeout(timer)
  }, [key, canQuery, retryAttempt])

  return useMemo(
    () => ({
      probe: answer ? answer.probe : undefined,
      key: answer?.key ?? null,
      pending: key !== null && failedKey !== key && answer?.key !== key,
      error: key !== null && failedKey === key,
      retry,
    }),
    [answer, key, failedKey, retry],
  )
}
